import { Component, OnInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { WALLET_REPORT_COLUMN_DEFS, WALLET_REPORT_TYPE_OPTIONS } from '../config/wallet-report.config';
import { IWalletReportDTO, IWalletReportFilter } from '../models/wallet-report.models';
import WalletReportService from '../services/wallet-report.service';
import { environment } from './../../../../../environments/environment';

const { defaultPageSizeArr } = environment;

@Component({
  selector: 'app-wallet-report',
  templateUrl: './wallet-report.component.html',
  styleUrls: ['./wallet-report.component.scss'],
})
export class WalletReportComponent implements OnInit {
  public title = 'Wallet Balance Report';
  public subtitle = 'View merchant and customer wallet balances for a given date';

  public pageSize = 10;
  public currentPage = 1;
  public collectionSize = 0;
  public loading = false;
  public pageSizeArr: Array<number> = defaultPageSizeArr;

  public page_no = 0;
  public page_size = 10;

  public walletTypeOptions = WALLET_REPORT_TYPE_OPTIONS;
  public selectedDate: string = this.formatDate(new Date());
  public selectedWalletType: string | null = null;
  public mobileNumber = '';

  public rowData: IWalletReportDTO[] = [];
  public columnDefs: ColDef[] = WALLET_REPORT_COLUMN_DEFS;

  public defaultColDef: ColDef = {
    flex: 1,
    minWidth: 180,
    resizable: true,
    sortable: true,
    filter: false,
    autoHeight: true,
  };

  private gridApi!: GridApi<IWalletReportDTO>;

  constructor(private walletReportService: WalletReportService) {}

  ngOnInit(): void {
    this.search();
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildFilterExpressions(): string[] {
    const filters: string[] = [];
    if (this.selectedWalletType) {
      filters.push(`walletType:eq:${this.selectedWalletType}`);
    }
    if (this.mobileNumber && this.mobileNumber.trim() !== '') {
      filters.push(`mobileNumber:eq:${this.mobileNumber.trim()}`);
    }
    return filters;
  }

  search(): void {
    if (!this.selectedDate) {
      return;
    }

    this.loading = true;
    this.page_no = this.currentPage - 1;
    this.page_size = this.pageSize;

    const filter: IWalletReportFilter = {
      date: this.selectedDate,
      page_no: this.page_no,
      page_size: this.page_size,
      filters: this.buildFilterExpressions()
    };

    this.walletReportService.getWalletReport(filter).subscribe({
      next: (response) => {
        const page = response?.response?.data;
        this.rowData = page?.content ?? [];
        this.collectionSize = page?.totalElements ?? this.rowData.length;
        this.loading = false;
      },
      error: () => {
        this.rowData = [];
        this.collectionSize = 0;
        this.loading = false;
      },
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.search();
  }

  onReset(): void {
    this.selectedDate = this.formatDate(new Date());
    this.selectedWalletType = null;
    this.mobileNumber = '';
    this.currentPage = 1;
    this.search();
  }

  onGridReady(params: GridReadyEvent<IWalletReportDTO>): void {
    this.gridApi = params.api;
  }

  onPaginationChange(pageNumber: number): void {
    if (pageNumber !== this.currentPage) {
      this.currentPage = pageNumber;
      this.search();
    }
  }

  onPageSizeChanges($event: Event): void {
    const value = Number(($event.target as HTMLSelectElement).value);
    if (!Number.isFinite(value) || value <= 0 || value === this.pageSize) {
      return;
    }

    this.pageSize = value;
    this.currentPage = 1;
    this.gridApi?.paginationSetPageSize(value);
    this.search();
  }

  onExportCSV(): void {
    if (!this.selectedDate) {
      return;
    }

    this.loading = true;
    this.walletReportService.exportWalletReportCSV(this.selectedDate).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wallet-report_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error exporting wallet report CSV:', error);
        this.loading = false;
      },
    });
  }
}
